import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import logo from "../../Assets/Logo(1).svg";
import AuthUser from "../../Hooks/authUser";
import moment from "moment";

/* ─── small hook: fires when clicked outside a ref element ─── */
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

const NAV_LINKS = [
  { label: "Home", to: "/home" },
  { label: "Plans", to: "/pricing" },
  { label: "About Us", to: "/aboutUs" },
  { label: "Accessories", to: "/accessories" },
];

const DOCTOR_LINKS = [
  { label: "Lectures", to: "/lectures", icon: "solar:play-circle-linear" },
  { label: "Researches", to: "/researches-brochure", icon: "solar:document-text-linear" },
  { label: "Brochure", to: "/brochure", icon: "solar:file-linear" },
];

function Navbar() {
  const { userInfo, logout } = AuthUser();
  const location = useLocation();
  const issueDate = moment().format("YYYY-MM-DD");
  const [packages, setPackages] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const dropdownRef = useRef(null);
  useClickOutside(dropdownRef, () => setDoctorOpen(false));

  const isExpire = packages?.expireDate
    ? moment(issueDate).isAfter(packages?.expireDate)
    : true;

  /* scroll detection — adds backdrop-blur & shadow after 12px */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* close mobile menu on route change */
  useEffect(() => {
    setMobileOpen(false);
    setDoctorOpen(false);
  }, [location.pathname]);

  /* load user package */
  useEffect(() => {
    if (!userInfo?._id) return;
    fetch(`https://servercareseebd.careseebd.com/api/v1/package/specific?fieldName=userId&&fieldValue=${userInfo._id}`)
      .then((r) => r.json())
      .then((d) => { if (d?.data?.length) setPackages(d.data[0]); });
  }, [userInfo?._id]);

  const isActive = (path) => location.pathname === path;

  /* ─── auth end-cap ─── */
  const AuthCap = ({ mobile = false }) => {
    const loggedIn = !!localStorage.getItem("medmyneAccess");
    const expiredUser = userInfo?.role === "user" && isExpire;

    if (!loggedIn) {
      return (
        <Link
          to="/login"
          className={mobile
            ? "block w-full text-center py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
            : "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold transition-colors shadow-sm shadow-indigo-600/20"
          }
        >
          Login
        </Link>
      );
    }

    if (expiredUser) {
      return (
        <div className="flex items-center gap-2">
          <Link to="/pricing" className={mobile
            ? "block w-full text-center py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
            : "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold transition-colors shadow-sm shadow-indigo-600/20"
          }>Upgrade</Link>
          <button
            onClick={logout}
            className={mobile
              ? "block w-full text-center py-2.5 rounded-xl bg-rose-50 text-rose-600 text-sm font-semibold hover:bg-rose-100 transition-colors border border-rose-100"
              : "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 text-[13px] font-semibold transition-colors border border-rose-100 cursor-pointer"
            }
          >
            <Icon icon="solar:logout-2-linear" className="w-4 h-4" />
            Log Out
          </button>
        </div>
      );
    }

    return (
      <Link
        to={`/${userInfo?.role}Dashboard`}
        className={mobile
          ? "block w-full text-center py-2.5 rounded-xl bg-indigo-50 text-indigo-600 text-sm font-semibold hover:bg-indigo-100 transition-colors border border-indigo-100"
          : "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[13px] font-semibold transition-colors border border-indigo-100"
        }
      >
        <Icon icon="solar:widget-linear" className="w-4 h-4" />
        Dashboard
      </Link>
    );
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm"
        : "bg-white border-b border-gray-100/60"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* ── Logo ── */}
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="Medmyne" className="h-8 w-auto" />
          </Link>

          {/* ── Desktop Nav Links ── */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3.5 py-2 rounded-lg text-[13.5px] font-medium transition-colors ${isActive(l.to)
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
              >
                {l.label}
              </Link>
            ))}

            {/* Doctor dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDoctorOpen((p) => !p)}
                className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-[13.5px] font-medium transition-colors ${doctorOpen
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
              >
                For Doctor
                <Icon
                  icon="solar:alt-arrow-down-linear"
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${doctorOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown panel */}
              {doctorOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-100 rounded-2xl shadow-xl shadow-gray-200/60 overflow-hidden py-1.5">
                  {DOCTOR_LINKS.map((dl) => (
                    <Link
                      key={dl.to}
                      to={dl.to}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                    >
                      <Icon icon={dl.icon} className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      {dl.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Desktop Auth ── */}
          <div className="hidden lg:flex items-center gap-3">
            <AuthCap />
          </div>

          {/* ── Mobile Hamburger ── */}
          <button
            onClick={() => setMobileOpen((p) => !p)}
            className="lg:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <Icon
              icon={mobileOpen ? "solar:close-square-linear" : "solar:hamburger-menu-linear"}
              className="w-5.5 h-5.5"
            />
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1 shadow-lg">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive(l.to)
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
            >
              {l.label}
            </Link>
          ))}

          {/* Mobile Doctor accordion */}
          <div>
            <button
              onClick={() => setDoctorOpen((p) => !p)}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              For Doctor
              <Icon
                icon="solar:alt-arrow-down-linear"
                className={`w-4 h-4 transition-transform duration-200 ${doctorOpen ? "rotate-180" : ""}`}
              />
            </button>
            {doctorOpen && (
              <div className="mt-1 ml-4 pl-3 border-l-2 border-indigo-100 space-y-0.5">
                {DOCTOR_LINKS.map((dl) => (
                  <Link
                    key={dl.to}
                    to={dl.to}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                  >
                    <Icon icon={dl.icon} className="w-4 h-4 text-gray-400" />
                    {dl.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-gray-100 mt-2">
            <AuthCap mobile />
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
